import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { contentToString, sliceContent } from '@next-space/fe-inlined';
import { getEditorModelByEditorKey } from 'src/editor/editor/uikit/editable-models';
import { segmentsToText } from 'src/editor/utils/editor';
import { convertContentToSegments } from 'src/editor/utils/segments';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { archiveBlock } from 'src/redux/managers/block/archive';
import type { UIState } from 'src/redux/reducers/ui';
import { cache } from 'src/redux/store';
import type { SelectBlock } from 'src/redux/types';
import { $appUiStateCache } from 'src/services/app';
import { filterDescendantId, getSyncBlockBackLinkInfo } from 'src/utils/block-utils';
import { handleSpace, writeImageInClipboard } from 'src/utils/clipboard';
import { parseEditorKeyToBlockId } from 'src/utils/editor-key-to-id';
import { getEditorKeyFromElement } from 'src/utils/editor-utils';
import { getDynamicPageId } from 'src/utils/getPageId';
import { imageElementToBlob } from 'src/utils/image-utils';
import { markdownToHtml } from 'src/utils/ast-util';
import { selectedBlocksToIds } from 'src/utils/select-block-util';
import { check, isTableRow } from '../helper';
import type { CutCopyHook, NextSpaceInfo } from '../types';
import { Action, TEXT_BLOCKS, TEXT_CELLS, TEXT_CONTENT, TEXT_HTML, TEXT_PLAIN } from '../types';
import { convertText } from './convert-text';
import {
  convertTableCells,
  convertTableToHtml,
  covertBlocksToNextSpaceInfo,
  covertCellsToNextSpaceInfo,
} from './get-next-space-info';
import { setCutStore } from './cut-store';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';

export const onCutCopy =
  ({
    transaction,
    selectedBlockHistory,
    closeModal,
    clearSelectedCellValue,
    openDeleteSyncBlockDialog,
    readonly,
    deleteSelectedTableCells,
  }: CutCopyHook) =>
  (action: Action) =>
  (event: ClipboardEvent) => {
    const { selectedCells } = cache.ui;
    closeModal?.($appUiStateCache.$blockMenuListId);
    const selectedBlocks = filterDescendantId(cache.ui.selectedBlocks);
    if (check()) return;
    const pageId = getDynamicPageId();
    const { role, allowDuplicate } = getPermissions(pageId);

    if (
      (action === Action.CUT && (role === PermissionRole.READER || role === PermissionRole.NONE)) ||
      // 是分享页面禁止 copy 的情况
      (readonly && !allowDuplicate)
    ) {
      return;
    }

    // safari 和 chrome 的 activeElement 行为不一致
    const editorKey = getEditorKeyFromElement(document.activeElement);
    // const editorKey = document.activeElement?.getAttribute('data-editor');
    const isInMindMap = !!document.activeElement?.closest('[data-mind-map-id]');
    const id: string | undefined | null = parseEditorKeyToBlockId(editorKey ?? '');

    // 先清空之前的
    event.clipboardData?.clearData();
    setCutStore(undefined);
    const abort = () => {
      // 只有我们确定处理的操作才终止事件传递
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const locked = cache.blocks[getDynamicPageId()]?.data.format?.locked;

    const setPlainText = (text: string) => {
      event.clipboardData?.setData(TEXT_PLAIN, handleSpace(text));
    };

    const setBlocksToClipboard = (blockIds: SelectBlock[]) => {
      const data = covertBlocksToNextSpaceInfo(blockIds);

      event.clipboardData?.setData(
        TEXT_BLOCKS,
        JSON.stringify({ ...data, fromType: action, pageId: getDynamicPageId() })
      );

      const ids = selectedBlocksToIds(blockIds);
      const text = convertText(ids);
      setPlainText(text);
      event.clipboardData?.setData(TEXT_HTML, markdownToHtml(text));
      // 图片塞进剪切版,网上说firefox不生效，市面上也没什么好的解决方案。
      if (blockIds.length === 1 && cache.ui.selectedBlocks.length === 1) {
        const block = cache.blocks[blockIds[0]?.blockId ?? ''];
        const isExternalImage =
          block?.type === BlockType.EXTERNAL_FILE && block.data.display === 'image';
        if ((block?.type === BlockType.FILE && block.data.display === 'image') || isExternalImage) {
          const element = document.querySelector(`[data-block-id="${block.uuid}"] img`);
          if (element instanceof HTMLImageElement) {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.src = element.src;
            image.onload = () => {
              void imageElementToBlob(image).then((blob) => {
                if (blob) {
                  void writeImageInClipboard(blob);
                }
              });
            };
          }
        }
      }
      if (!readonly && action === Action.CUT && !locked) {
        transaction(() => {
          archiveBlock(ids);
          setCutStore({
            blockIds: ids,
            spaceId: getCurrentSpaceId(),
          });
          selectedBlockHistory?.([], selectedBlocks);
        });
      }
    };

    const setCellsToClipboard = (selectedCells: UIState['selectedCells']) => {
      const spaceInfo = covertCellsToNextSpaceInfo(selectedCells);
      if (!spaceInfo) return;
      event.clipboardData?.setData(
        TEXT_CELLS,
        JSON.stringify({ records: spaceInfo.records } as NextSpaceInfo)
      );
      setPlainText(spaceInfo.plainText);
      if (!readonly && action === Action.CUT && !locked) {
        // 清空cell
        clearSelectedCellValue?.(cache.ui.selectedCells);
      }
    };
    // 在思维导图编辑态下不能剪切掉
    const isMindMapInEditMode = isInMindMap && id;

    if (selectedBlocks.length && !isMindMapInEditMode) {
      const backInfo = getSyncBlockBackLinkInfo(selectedBlocks);
      if (backInfo && action === Action.CUT) {
        openDeleteSyncBlockDialog?.(backInfo.onlyOne, () => {
          setBlocksToClipboard(selectedBlocks);
        });
      } else {
        setBlocksToClipboard(selectedBlocks);
      }
      abort();
      return;
    }

    if (selectedCells.length) {
      // 先简单处理下 简单表格 里的复制
      if (isTableRow()) {
        if (editorKey) {
          const editorModel = getEditorModelByEditorKey(editorKey);
          if (editorModel?.selection) {
            const { selection } = editorModel;
            const { isCollapsed } = selection;
            if (!isCollapsed) {
              const { anchorOffset, focusOffset } = selection;
              const content = sliceContent(
                editorModel.content,
                anchorOffset <= focusOffset ? anchorOffset : focusOffset,
                anchorOffset > focusOffset ? anchorOffset : focusOffset
              );
              setPlainText(contentToString(content));
              event.clipboardData?.setData(TEXT_CONTENT, JSON.stringify({ content }));
              if (!readonly && action === Action.CUT && !locked) {
                editorModel.performChange((ctx) => ctx.delete());
              }
            }
          }
        } else {
          const info = convertTableCells(selectedCells);
          if (!info) return;

          const { plain, records } = info;

          setPlainText(plain);
          event.clipboardData?.setData(TEXT_CELLS, JSON.stringify({ records }));
          event.clipboardData?.setData(TEXT_HTML, convertTableToHtml(records));

          if (!readonly && action === Action.CUT && !locked) {
            deleteSelectedTableCells?.();
          }
        }
      } else {
        setCellsToClipboard(selectedCells);
      }

      abort();
      return;
    }

    // 有 id 说明光标在富文本里
    if (id) {
      const editorModel = getEditorModelByEditorKey(editorKey ?? '');
      if (editorModel?.selection) {
        const { selection } = editorModel;
        const { isCollapsed } = selection;

        if (isCollapsed) {
          // 在title上复制剪切会有问题，需要过滤掉
          if (getDynamicPageId() === id) {
            return;
          }
          setBlocksToClipboard([{ blockId: id }]);
        } else {
          const { anchorOffset, focusOffset } = selection;
          const content = sliceContent(
            editorModel.content,
            anchorOffset <= focusOffset ? anchorOffset : focusOffset,
            anchorOffset > focusOffset ? anchorOffset : focusOffset
          );
          event.clipboardData?.setData(
            TEXT_PLAIN,
            segmentsToText(convertContentToSegments(content))
          );
          event.clipboardData?.setData(TEXT_CONTENT, JSON.stringify({ content }));
          if (!readonly && action === Action.CUT && !locked) {
            editorModel.performChange((ctx) => ctx.delete());
          }
        }
      }
      abort();
    }
  };
